import { action, makeObservable, observable } from 'mobx';
import { isMobile } from '../../helpers/isMobile';
import {
  oddsInfoEnumsByGame,
  payTableStepsContentByGame,
} from '../../constants/payTableConstants';
import { gameResources } from '../../constants/gameResources';
import { soundResources } from '../../constants/soundResources';
import { appDefaultValuesEnum } from '../../constants/appDefaultValues';
import { gameNameById, winPopupConfigs } from '../../constants/enumsByGames';
import {
  reelParamsByGame,
  reelsContainerParamsByGame,
  symbolSizesByGame,
} from '../../constants/reelConstants';
import { initialGameResources } from '../../constants/initialResources';

// RU: replaymode приходит из parseQuery СТРОКОЙ, поэтому "0" и "false" тоже truthy: без
//     приведения к boolean обычный запуск уходил в ReplayMode ядра (MIGRATION-CLASSIC §2.2).
const isReplayFlag = (replaymode) =>
  replaymode === true || ['1', 'true'].includes(String(replaymode ?? '').trim().toLowerCase());

const knownGameIds = Object.keys(gameNameById);

class GameConfigurationStore {
  gameConfigurationisReady = false;
  gameConfiguration = {};
  isReplay = false;
  gameId = null;
  reelsParams = null;
  symbolsSizes = null;
  reelsContainerParams = null;
  constructor() {
    makeObservable(this, {
      gameId: observable,
      isReplay: observable,
      reelsParams: observable,
      symbolsSizes: observable,
      reelsContainerParams: observable,
      gameConfigurationisReady: observable,
      setGameConfigurationisReady: action.bound,
      createGameConfigurationTree: action.bound,
      createReelsParams: action.bound,
      setSymbolsSizes: action.bound,
      setReelsContainerParams: action.bound,
    });
  }

  // RU: без известного gameid собирать нечего — все таблицы constants/* по нему. Ядро ещё не
  //     смонтировано, и сообщить лаунчеру некому: отказ — явная ошибка в консоли и пустая сцена
  //     (прежде неизвестный id ронял запуск TypeError-ом на первой же таблице).
  createGameConfigurationTree = ({ gameid, replaymode }) => {
    if (!knownGameIds.includes(String(gameid))) {
      console.error(
        `[pumpkinPayday] ${gameid ? `unknown gameid "${gameid}"` : 'the launch query has no gameid'}: ` +
          `the game will not load. Known gameids: ${knownGameIds.join(', ')}.`
      );
      return;
    }
    this.gameId = gameid;
    this.isReplay = isReplayFlag(replaymode);
    this.createGameConfigs();
    this.setSymbolsSizes();
    this.setReelsContainerParams();
    this.createReelsParams();
    this.setGameConfigurationisReady(true);
  };

  createGameConfigs = () => {
    const gameName = gameNameById[this.gameId];
    this.gameConfiguration = {
      oddsInfoEnum:
        oddsInfoEnumsByGame[this.gameId][isMobile() ? 'oddsInfoEnumMobile' : 'oddsInfoEnumWeb'],
      payTableStepsContent: payTableStepsContentByGame[this.gameId],
      // RU: коэффициенты пейтейбла ядро 3.x берёт ТОЛЬКО отсюда.
      // RU: payTableOdds не задан — коэффициенты пейтейбла ядро берёт из InitialState.oddsInfo сервера
      //     (решение владельца 05.10); oddsInfoEnum[i].id — индекс серверного массива.
      // RU: тип фона и лого — явно: умолчание ядра (undefined ⇒ spine) совпадает с этой игрой
      //     лишь случайно, и смена умолчания молча увела бы их в другую ветку.
      // RU: фон по платформе, как в 2.x. Веб — анимированный спайн `mainBg` ядра (в нём вся сцена).
      //     Телефон — спрайт ядра (кадр `background` атласа игры, 540×960): мобильный `mainBg` — риг без
      //     слота с картинкой, и со спайновым типом на телефоне фон был чёрным. Анимированного слоя
      //     поверх спрайта на телефоне в 2.x не было, его не добавляем.
      // EN: background per platform, as in 2.x. Web is the core's animated `mainBg` spine (the whole
      //     scene). Phones use the core's sprite (the game atlas frame `background`, 540x960): the
      //     mobile `mainBg` rig has no picture slot, so with the spine type the phone background was
      //     black. 2.x drew no animated layer over the sprite on phones, so none is added.
      bgConfigs: isMobile()
        ? { type: 'sprite', x: 0, y: 0 }
        : { type: 'spine', defaultAnimation: 'animation', x: 720, y: 405 },
      logoConfigs: {
        type: 'spine',
        // RU: имя анимации лого — данные игры (ядро своих имён арта не знает).
        defaultAnimation: 'logoAnimation',
        x: isMobile() ? 268 : 720,
        y: isMobile() ? 200 : 83,
      },
      soundResources,
      initialGameResources: initialGameResources(gameName),
      gameResources: gameResources(gameName),
      gameName,
      AppDefaultValuesEnum: appDefaultValuesEnum(this.gameId),
      isReplay: this.isReplay,
      // RU: панель ставок — core-дефолтный пак, вариант default (покупки бонуса у игры нет).
      bettingPartType: 'default',
      // RU: автоигра — модалка Autoplay Settings ядра, единственная (ключ autoPlayUi снят 01.10
      //     вместе со старым счётчиком).
      // RU: анимация загрузки у платформ названа по-разному (web — `loadingAnimation`,
      //     mobile — `loading`); кость прогресса — `progress`.
      loadingConfig: {
        spineKey: 'loading',
        // RU: кости вписывания рига загрузки (ядро вписывает только на телефоне).
        backgroundBone: 'background',
        contentBone: 'scale2',
        actions: [
          { type: 'loop', trackIndex: 0, animation: isMobile() ? 'loading' : 'loadingAnimation' },
          { type: 'progressBone', bone: 'progress', axis: 'x' },
        ],
      },
      winPopupConfigs,
    };
  };

  // RU: каждому барабану — СВОЙ объект параметров: `Array(n).fill(obj)` раздавал всем барабанам
  //     один и тот же объект, и позиции иконок одного барабана писались в общие для всех массивы.
  createReelsParams = () => {
    const { reelParams, reelsCount } = reelParamsByGame[this.gameId];
    this.reelsParams = Array.from({ length: reelsCount }, () => ({
      ...reelParams,
      iconsFinalPositions: { inWinContainer: [], inReel: [] },
    }));
  };

  setSymbolsSizes = () => {
    this.symbolsSizes = symbolSizesByGame[this.gameId];
  };

  setReelsContainerParams = () => {
    this.reelsContainerParams = reelsContainerParamsByGame[this.gameId];
  };

  setGameConfigurationisReady = (status) => {
    this.gameConfigurationisReady = status;
  };
}

export const gameConfigurationStore = new GameConfigurationStore();
